import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, Plus, LogOut, MapPin, Phone, Globe, Compass, User, Trash2, Pencil, Settings } from "lucide-react";
import { toast } from "sonner";
import { useSiteBranding } from "../contexts/SiteBrandingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ isAuthenticated, user, onLogout }) => {
  const [ads, setAds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { branding } = useSiteBranding();

  // Filter states
  const [keyword, setKeyword] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategories, setSelectedSubcategories] = useState([]); // Changed to array for multi-select
  const [selectedServiceAreas, setSelectedServiceAreas] = useState([]); // Changed to array for multi-select
  const [subcategories, setSubcategories] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchAds();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      const category = categories.find(cat => cat.name === selectedCategory);
      setSubcategories(category?.subcategories || []);
      setSelectedSubcategories([]); // Clear array
    } else {
      setSubcategories([]);
      setSelectedSubcategories([]); // Clear array
    }
  }, [selectedCategory, categories]);

  // Auto-filter when filter values change
  useEffect(() => {
    // Only trigger if at least one filter is set (to avoid fetching on initial render)
    if (selectedCategory || selectedSubcategories.length > 0 || selectedServiceAreas.length > 0) {
      fetchAds();
    }
  }, [selectedCategory, selectedSubcategories, selectedServiceAreas]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
      setServiceAreas(response.data.service_areas || []);
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  const fetchAds = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        limit: 20
      };
      if (keyword) params.keyword = keyword;
      if (selectedCategory) params.general_category = selectedCategory;
      if (selectedSubcategories.length > 0) {
        params.subcategory = selectedSubcategories.join(',');
      }
      if (selectedServiceAreas.length > 0) {
        params.service_area = selectedServiceAreas.join(',');
      }

      const response = await axios.get(`${API}/ads`, { params });
      setAds(response.data.ads);
      setCurrentPage(response.data.pagination.page);
      setTotalPages(response.data.pagination.total_pages);
      setTotalCount(response.data.pagination.total_count);
      setHasNext(response.data.pagination.has_next);
      setHasPrev(response.data.pagination.has_prev);
    } catch (error) {
      toast.error("Failed to fetch ads");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchAds(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchAds(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = async () => {
    setKeyword("");
    setSelectedCategory("");
    setSelectedSubcategories([]); // Changed to empty array
    setSelectedServiceAreas([]); // Changed to empty array
    setCurrentPage(1);
    
    // Immediately fetch ads with cleared filters
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ads`, { params: { page: 1, limit: 20 } });
      setAds(response.data.ads);
      setCurrentPage(response.data.pagination.page);
      setTotalPages(response.data.pagination.total_pages);
      setTotalCount(response.data.pagination.total_count);
      setHasNext(response.data.pagination.has_next);
      setHasPrev(response.data.pagination.has_prev);
    } catch (error) {
      toast.error("Failed to fetch ads");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAd = async (adId) => {
    console.log("Delete button clicked for ad:", adId);
    console.log("Current user:", user);
    console.log("Admin email from env:", process.env.REACT_APP_ADMIN_EMAIL);
    
    try {
      const token = localStorage.getItem("token");
      console.log("Token exists:", !!token);
      
      await axios.delete(`${API}/ads/${adId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      toast.success("Ad deleted successfully");
      fetchAds(); // Refresh the ads list
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete ad");
    }
  };

  // If not authenticated, show landing page with auth options
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full space-y-12 text-center">
          <div className="space-y-6">
            <div className="inline-flex items-center justify-center w-48 h-48 rounded-3xl mb-8 shadow-2xl transform hover:scale-105 transition-transform duration-300" style={{ backgroundColor: branding.logo_bg_color || '#000000' }}>
              {branding.logo_url ? (
                <img 
                  src={`${BACKEND_URL}${branding.logo_url}`} 
                  alt="Logo" 
                  className="w-full h-full object-contain rounded-3xl"
                />
              ) : (
                <span className="text-white text-5xl font-bold">ESMC</span>
              )}
            </div>
            <h1 className="text-6xl sm:text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-black to-gray-700 mb-6 leading-tight">
              {branding.site_title}
            </h1>
          </div>

          <div className="space-y-5">
            <Button
              onClick={() => navigate("/login")}
              data-testid="landing-login-button"
              className="w-full h-16 sm:h-18 bg-gradient-to-r from-black to-gray-900 text-white hover:from-gray-900 hover:to-black text-xl font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 rounded-xl"
            >
              Sign In
            </Button>

            <Button
              onClick={() => navigate("/register")}
              data-testid="landing-register-button"
              className="w-full h-16 sm:h-18 bg-gray-700 text-white hover:bg-gray-800 text-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 rounded-xl"
            >
              Create Account
            </Button>
          </div>

          <p className="text-base text-gray-500 mt-10">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-200">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-200 sticky top-0 z-50 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 sm:py-0 sm:h-24">
            <div className="flex items-center justify-between sm:justify-start">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg transform hover:scale-105 transition-transform duration-300" style={{ backgroundColor: branding.logo_bg_color || '#000000' }}>
                  {branding.logo_url ? (
                    <img 
                      src={`${BACKEND_URL}${branding.logo_url}`} 
                      alt="Logo" 
                      className="w-full h-full object-contain rounded-2xl"
                    />
                  ) : (
                    <span className="text-white text-2xl sm:text-3xl font-bold">CA</span>
                  )}
                </div>
                <div className="flex flex-col">
                  <h1 className="text-2xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-black to-gray-700 truncate">{branding.site_title}</h1>
                  {isAuthenticated && (
                    <span className="text-gray-600 font-medium text-xs sm:hidden mt-1">Welcome, {user?.name}</span>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2 sm:hidden ml-2">
                {isAuthenticated && (
                  <>
                    {user?.email === process.env.REACT_APP_ADMIN_EMAIL && (
                      <>
                        <Button
                          onClick={() => navigate("/admin/settings")}
                          data-testid="admin-settings-button"
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-100 rounded-xl"
                        >
                          <Settings className="w-5 h-5" />
                        </Button>
                        <Button
                          onClick={() => navigate("/create-ad")}
                          data-testid="create-ad-button"
                          size="icon"
                          className="h-10 w-10 bg-gradient-to-r from-black to-gray-900 text-white hover:from-gray-900 hover:to-black rounded-xl"
                        >
                          <Plus className="w-5 h-5" />
                        </Button>
                      </>
                    )}
                    <Button
                      onClick={onLogout}
                      variant="outline"
                      size="icon"
                      data-testid="logout-button"
                      className="h-10 w-10 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-100 rounded-xl"
                    >
                      <LogOut className="w-5 h-5" />
                    </Button>
                  </>
                )}
                {!isAuthenticated && (
                  <>
                    <Button
                      onClick={() => navigate("/login")}
                      variant="outline"
                      className="h-10 px-4 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-100 rounded-xl text-sm font-medium"
                    >
                      Sign In
                    </Button>
                    <Button
                      onClick={() => navigate("/register")}
                      className="h-10 px-4 bg-gradient-to-r from-black to-gray-900 text-white hover:from-gray-900 hover:to-black rounded-xl text-sm font-semibold"
                    >
                      Register
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="hidden sm:flex items-center space-x-2 sm:space-x-3">
              {isAuthenticated ? (
                <>
                  <span className="text-gray-700 font-medium text-sm sm:text-base">Welcome, {user?.name}</span>
                  {user?.email === process.env.REACT_APP_ADMIN_EMAIL && (
                    <>
                      <Button
                        onClick={() => navigate("/admin/settings")}
                        data-testid="admin-settings-button"
                        variant="outline"
                        className="h-11 sm:h-12 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-100 transition-all shadow-sm hover:shadow-md transform hover:scale-105 duration-200 rounded-xl hidden sm:flex px-4 sm:px-6 text-base font-medium"
                      >
                        <Settings className="w-5 h-5 sm:mr-2" />
                        <span className="hidden sm:inline">Settings</span>
                      </Button>
                      <Button
                        onClick={() => navigate("/create-ad")}
                        data-testid="create-ad-button"
                        className="h-11 sm:h-12 bg-gradient-to-r from-black to-gray-900 text-white hover:from-gray-900 hover:to-black transition-all shadow-lg hover:shadow-xl transform hover:scale-105 duration-200 rounded-xl px-4 sm:px-6 text-base font-semibold"
                      >
                        <Plus className="w-5 h-5 sm:mr-2" />
                        <span className="hidden sm:inline">Post Ad</span>
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    onClick={onLogout}
                    data-testid="logout-button"
                    className="h-11 sm:h-12 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-100 transition-all shadow-sm hover:shadow-md transform hover:scale-105 duration-200 rounded-xl px-3 sm:px-4"
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/login")}
                    data-testid="header-login-button"
                    className="h-11 sm:h-12 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-100 transition-all shadow-sm hover:shadow-md transform hover:scale-105 duration-200 rounded-xl px-4 sm:px-6 text-base font-medium"
                  >
                    Sign In
                  </Button>
                  <Button
                    onClick={() => navigate("/register")}
                    data-testid="header-register-button"
                    className="h-11 sm:h-12 bg-gradient-to-r from-black to-gray-900 text-white hover:from-gray-900 hover:to-black transition-all shadow-lg hover:shadow-xl transform hover:scale-105 duration-200 rounded-xl px-4 sm:px-6 text-base font-semibold"
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        {/* Banner Image */}
        {branding.banner_url && (
          <div className="mb-6 sm:mb-8 rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
            <img
              src={`${BACKEND_URL}${branding.banner_url}`}
              alt="Banner"
              className="w-full h-32 sm:h-48 object-cover"
            />
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-gray-100 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-8 mb-6 sm:mb-8">
          <div className="space-y-4 sm:space-y-6">
            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-5 sm:w-6 h-5 sm:h-6 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search services..."
                  data-testid="search-keyword-input"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 sm:pl-14 h-12 sm:h-14 border-2 border-gray-300 bg-white focus:border-black transition-all text-base sm:text-lg"
                />
              </div>
              <Button
                onClick={handleSearch}
                data-testid="search-button"
                className="h-12 sm:h-14 px-6 sm:px-10 bg-black text-white hover:bg-gray-800 font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
              >
                <Search className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                Search
              </Button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label className="text-gray-700 text-sm sm:text-base font-semibold mb-1 sm:mb-2 block">Category</label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger data-testid="category-select" className="h-11 sm:h-12 border-2 border-gray-300 bg-white hover:border-gray-400 transition-colors text-base">
                    <SelectValue placeholder="General Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.name} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-gray-700 text-sm sm:text-base font-semibold mb-1 sm:mb-2 block">Subcategory</label>
                <Select
                  value={selectedSubcategories.length > 0 ? "multi-selected" : ""}
                  onValueChange={(value) => {
                    if (value === "clear-all") {
                      setSelectedSubcategories([]);
                    } else if (value !== "multi-selected") {
                      // Toggle selection
                      if (selectedSubcategories.includes(value)) {
                        setSelectedSubcategories(selectedSubcategories.filter(sub => sub !== value));
                      } else {
                        setSelectedSubcategories([...selectedSubcategories, value]);
                      }
                    }
                  }}
                  disabled={!selectedCategory}
                >
                  <SelectTrigger data-testid="subcategory-select" className="h-11 sm:h-12 border-2 border-gray-300 bg-white hover:border-gray-400 transition-colors disabled:opacity-50 text-base">
                    <SelectValue placeholder="Subcategory">
                      {selectedSubcategories.length > 0 
                        ? `${selectedSubcategories.length} selected` 
                        : "Subcategory"
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {selectedSubcategories.length > 0 && (
                      <SelectItem value="clear-all" className="text-red-600 font-medium">
                        ✕ Clear All
                      </SelectItem>
                    )}
                    {subcategories.map((sub) => (
                      <SelectItem 
                        key={sub} 
                        value={sub}
                        className={`cursor-pointer ${selectedSubcategories.includes(sub) ? "bg-gray-100 font-medium" : ""}`}
                      >
                        <div className="flex items-center gap-2 w-full pointer-events-none">
                          <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 ${selectedSubcategories.includes(sub) ? "bg-black border-black" : "border-gray-300"}`}>
                            {selectedSubcategories.includes(sub) && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span>{sub}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-gray-700 text-sm sm:text-base font-semibold mb-1 sm:mb-2 block">Service Area</label>
                <Select 
                  value={selectedServiceAreas.length > 0 ? "multi-selected" : ""} 
                  onValueChange={(value) => {
                    if (value === "clear-all") {
                      setSelectedServiceAreas([]);
                    } else if (value !== "multi-selected") {
                      // Toggle selection
                      if (selectedServiceAreas.includes(value)) {
                        setSelectedServiceAreas(selectedServiceAreas.filter(area => area !== value));
                      } else {
                        setSelectedServiceAreas([...selectedServiceAreas, value]);
                      }
                    }
                  }}
                >
                  <SelectTrigger data-testid="service-area-select" className="h-11 sm:h-12 border-2 border-gray-300 bg-white hover:border-gray-400 transition-colors text-base">
                    <SelectValue placeholder="Service Area">
                      {selectedServiceAreas.length > 0 
                        ? `${selectedServiceAreas.length} selected` 
                        : "Service Area"
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {selectedServiceAreas.length > 0 && (
                      <SelectItem value="clear-all" className="text-red-600 font-medium">
                        ✕ Clear All
                      </SelectItem>
                    )}
                    {serviceAreas.map((area) => (
                      <SelectItem 
                        key={area} 
                        value={area}
                        className={`cursor-pointer ${selectedServiceAreas.includes(area) ? "bg-gray-100 font-medium" : ""}`}
                      >
                        <div className="flex items-center gap-2 w-full pointer-events-none">
                          <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 ${selectedServiceAreas.includes(area) ? "bg-black border-black" : "border-gray-300"}`}>
                            {selectedServiceAreas.includes(area) && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span>{area}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              variant="outline"
              onClick={handleClearFilters}
              data-testid="clear-filters-button"
              className="w-full sm:w-auto border-2 border-gray-400 text-gray-700 hover:bg-gray-200 hover:border-gray-500 transition-all"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Ads Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading ads...</p>
          </div>
        ) : ads.length === 0 ? (
          <div className="text-center py-8 sm:py-12 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-500 text-base sm:text-lg px-4">No ads found. Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {ads.map((ad) => (
              <div
                key={ad.id}
                data-testid={`ad-card-${ad.id}`}
                onClick={() => navigate(`/ad/${ad.id}`)}
                className="bg-white rounded-lg sm:rounded-xl shadow-md border border-gray-300 p-4 sm:p-6 hover:shadow-lg hover:border-gray-400 transition-all cursor-pointer active:scale-95 sm:hover:scale-[1.02]"
              >
                <div className="space-y-2 sm:space-y-3">
                  {/* Ad Image */}
                  {ad.image_url && (
                    <div className="mb-3 sm:mb-4">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${ad.image_url}`}
                        alt={ad.title}
                        className="w-full h-40 sm:h-48 object-cover rounded-lg shadow-lg"
                        loading="lazy"
                        onError={(e) => {
                          // Only log in development to avoid production monitoring noise
                          if (process.env.NODE_ENV === 'development') {
                            console.warn('Image failed to load:', ad.image_url);
                          }
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  <div className="flex items-start justify-between">
                    <h3 className="text-lg sm:text-xl font-semibold text-black line-clamp-2 flex-1" data-testid="ad-title">
                      {ad.title}
                    </h3>
                    <div className="flex items-center gap-1 sm:gap-2 ml-2">
                      {isAuthenticated && user?.email === process.env.REACT_APP_ADMIN_EMAIL && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/edit-ad/${ad.id}`);
                            }}
                            data-testid={`edit-ad-${ad.id}`}
                            className="h-8 w-8 flex-shrink-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Edit ad"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAd(ad.id);
                            }}
                            data-testid={`delete-ad-${ad.id}`}
                            className="h-8 w-8 flex-shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete ad"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <p className="text-gray-600 line-clamp-3 text-sm" data-testid="ad-description">
                    {ad.description}
                  </p>

                  <div className="pt-3 border-t border-gray-100 space-y-2">
                    <div className="text-sm">
                      <div className="flex items-start text-gray-500 mb-1">
                        <User className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                        <span className="text-xs font-medium">Road Name:</span>
                      </div>
                      <span className="text-gray-900 ml-5" data-testid="ad-roadname">{ad.contact_roadname}</span>
                    </div>
                    
                    {ad.service_area && ad.service_area.length > 0 && (
                      <div className="text-sm">
                        <div className="flex items-start text-gray-500 mb-1">
                          <Compass className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                          <span className="text-xs font-medium">Service Area:</span>
                        </div>
                        <div className="ml-5 flex flex-wrap gap-1">
                          {ad.service_area.filter(area => area && area.trim()).map((area, index) => (
                            <span key={index} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-testid={`ad-service-area-${index}`}>
                              {area}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="text-sm">
                      <div className="flex items-start text-gray-500 mb-1">
                        <Compass className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                        <span className="text-xs font-medium">Location:</span>
                      </div>
                      <span className="text-gray-900 ml-5" data-testid="ad-location">{ad.location}</span>
                    </div>
                    
                    <div className="text-sm">
                      <div className="flex items-start text-gray-500 mb-1">
                        <Phone className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                        <span className="text-xs font-medium">Phone:</span>
                      </div>
                      <span className="text-gray-900 ml-5" data-testid="ad-phone">{ad.contact_phone}</span>
                    </div>
                    
                    {ad.website_link && (
                      <div className="text-sm">
                        <div className="flex items-start text-gray-500 mb-1">
                          <Globe className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                          <span className="text-xs font-medium">Website:</span>
                        </div>
                        <a
                          href={ad.website_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline text-black truncate ml-5"
                          data-testid="ad-website"
                        >
                          {ad.website_link}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Social Media Icons */}
                  {(ad.discord_link || ad.instagram_link || ad.facebook_link || ad.telegram_link || ad.whatsapp_link || ad.linkedin_link) && (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="flex gap-3">
                        {ad.discord_link && (
                          <a
                            href={ad.discord_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-600 hover:text-indigo-600 transition-colors"
                            title="Discord"
                          >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.369-.444.85-.608 1.23a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.23A.077.077 0 0 0 8.562 3c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055 20.03 20.03 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026 13.83 13.83 0 0 0 1.226-1.963.074.074 0 0 0-.041-.104 13.201 13.201 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.245.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 0 0 .084.028 19.963 19.963 0 0 0 6.002-2.981.076.076 0 0 0 .032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z"/>
                            </svg>
                          </a>
                        )}
                        {ad.instagram_link && (
                          <a
                            href={ad.instagram_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-600 hover:text-pink-600 transition-colors"
                            title="Instagram"
                          >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                            </svg>
                          </a>
                        )}
                        {ad.facebook_link && (
                          <a
                            href={ad.facebook_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-600 hover:text-blue-600 transition-colors"
                            title="Facebook"
                          >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                          </a>
                        )}
                        {ad.telegram_link && (
                          <a
                            href={ad.telegram_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-600 hover:text-blue-500 transition-colors"
                            title="Telegram"
                          >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                            </svg>
                          </a>
                        )}
                        {ad.whatsapp_link && (
                          <a
                            href={ad.whatsapp_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-600 hover:text-green-600 transition-colors"
                            title="WhatsApp"
                          >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                          </a>
                        )}
                        {ad.linkedin_link && (
                          <a
                            href={ad.linkedin_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-600 hover:text-blue-700 transition-colors"
                            title="LinkedIn"
                          >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2 text-xs mb-2">
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {ad.general_category}
                      </span>
                      {ad.subcategory && ad.subcategory.length > 0 && ad.subcategory.filter(sub => sub && sub.trim()).map((sub, index) => (
                        <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          {sub}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400">Posted by {ad.user_name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && ads.length > 0 && totalPages > 1 && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="text-sm text-gray-600">
              Showing <span className="font-semibold">{(currentPage - 1) * 20 + 1}</span> to{' '}
              <span className="font-semibold">{Math.min(currentPage * 20, totalCount)}</span> of{' '}
              <span className="font-semibold">{totalCount}</span> ads
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPrev}
                variant="outline"
                className="h-10 px-4 border-2 border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </Button>
              
              <div className="flex items-center gap-1">
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  // Show first page, last page, current page, and pages around current
                  const showPage = 
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);
                  
                  const showEllipsis = 
                    (pageNumber === 2 && currentPage > 3) ||
                    (pageNumber === totalPages - 1 && currentPage < totalPages - 2);
                  
                  if (showEllipsis) {
                    return <span key={pageNumber} className="px-2 text-gray-400">...</span>;
                  }
                  
                  if (!showPage) return null;
                  
                  return (
                    <Button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      variant={pageNumber === currentPage ? "default" : "outline"}
                      className={`h-10 w-10 ${
                        pageNumber === currentPage
                          ? "bg-black text-white hover:bg-gray-900"
                          : "border-2 border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      {pageNumber}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNext}
                variant="outline"
                className="h-10 px-4 border-2 border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;